/**
 * 板発注画面「自社過不足」セクションの表示専用集計 (AC-BIN-026a、 #9069)。
 *
 * 入力の need・cover・executed は、 呼び出し元 (MarketBoardTab.tsx) が入札追加タブの
 * チャートと同じ `buildIntradayPositionSlots` で全エリア合算して出す。 板発注画面は
 * 入札追加タブ (単一エリア限定、 AC-BIN-012b) と違い、 エリア合算を許容する
 * (#7144 論点 4 決定 B、 2026-09-07 PdM 判断)。
 */
import { BiddingIntradayOrderStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import {
  type IntradayBidRow,
  isWithdrawnBidRow,
} from '~/pages/IntradayBidding/models/intradayBidRow'

export type IntradayGapDirection = 'shortage' | 'surplus' | 'balanced'

/** 選択コマの need・「入札済み」線・「全札 (下書き含む)」線の cover・約定量 (買 − 売、 符号付き、 MW)。 */
export type IntradayGapInput = {
  needMw: number
  /** AC-BIN-105 の「入札済み」線 (`PositionChartSlot.coverSubmittedMw`)。 */
  coverSubmittedMw: number
  /** AC-BIN-105 の「全札 (下書き含む)」線 (`PositionChartSlot.coverMw`)。 */
  coverMw: number
  executedMw: number
}

export type IntradayGapSegments = {
  /** 買方向 不足 / 売方向 余剰 / 過不足なし (= need の符号から判定)。 */
  direction: IntradayGapDirection
  /** 必要量の絶対値 (MW)。 */
  needAbsMw: number
  /** 約定 (MW)。 */
  matchedMw: number
  /** 入札のみ (入札済み) (MW)。 */
  bidOnlyPlacedMw: number
  /** 入札のみ (下書き等) (MW)。 */
  bidOnlyDraftMw: number
  /** 未カバー (MW)。 必要量のうち、 約定にも入札のみの 2 区分にも充てられていない分。 */
  uncoveredMw: number
}

/** 基準 (「入札済み」「全札 (下書き含む)」) ごとの入札のみ・未カバー (MW)。 */
export type IntradayGapBasisValues = {
  bidOnlyMw: number
  uncoveredMw: number
}

// `|| 0` は -0 を 0 に揃える (丸めで -0 になっても 'balanced' と判定させるため)。
const roundToTenthMw = (mw: number): number => Math.round(mw * 10) / 10 || 0

type AlignedGapInput = {
  direction: IntradayGapDirection
  needAbsMw: number
  executedMw: number
  coverSubmittedMw: number
  coverMw: number
}

// 0.1 MW 単位へ丸めてから方向を判定する (mock-app `ComputeSelectedKomaGap` と同じ)。
// エリア合算の浮動小数誤差 (0.1+0.2−0.3 ≒ 5.6e-17) で過不足ゼロのコマが
// 「買方向 不足 必要 0.0 MW」 と出るのを防ぐ。 need と逆向きのネットは、 埋めた量と
// 数えると実際には埋まっていない分が埋まって見えるので 0 とする (#8575 の EN 判断)。
function alignGapInput(input: IntradayGapInput): AlignedGapInput {
  const needMw = roundToTenthMw(input.needMw)
  const alignedAbsMw = (rawMw: number): number => {
    const mw = roundToTenthMw(rawMw)
    return Math.sign(mw) === Math.sign(needMw) ? Math.abs(mw) : 0
  }
  return {
    direction: needMw > 0 ? 'shortage' : needMw < 0 ? 'surplus' : 'balanced',
    needAbsMw: Math.abs(needMw),
    executedMw: alignedAbsMw(input.executedMw),
    coverSubmittedMw: alignedAbsMw(input.coverSubmittedMw),
    coverMw: alignedAbsMw(input.coverMw),
  }
}

type AlignedGapBases = {
  matchedMw: number
  submitted: IntradayGapBasisValues
  all: IntradayGapBasisValues
}

// `Math.max(0, ...)` は、 約定が cover を超えるコマの負値と、 丸め誤差の極小の負値を 0 にする。
function computeAlignedBases(aligned: AlignedGapInput): AlignedGapBases {
  const { needAbsMw } = aligned
  const matchedMw = Math.min(aligned.executedMw, needAbsMw)
  const basisValues = (coverMw: number): IntradayGapBasisValues => {
    const bidOnlyMw = Math.max(0, Math.min(coverMw, needAbsMw) - matchedMw)
    return {
      bidOnlyMw,
      uncoveredMw: Math.max(0, needAbsMw - matchedMw - bidOnlyMw),
    }
  }
  return {
    matchedMw,
    submitted: basisValues(aligned.coverSubmittedMw),
    all: basisValues(aligned.coverMw),
  }
}

/**
 * AC-BIN-026a の 4 区分。 約定が cover を超えるコマや、 下書き等の札で全札の cover が入札済みを
 * 下回るコマは、 手前の区分を減らさずその区分を 0 にする (Discussion「横棒の区分・凡例・
 * 読み上げが AC-BIN-105 の線の値とずれるコマの描き方」)。
 *
 * need=0 (過不足なし) では全区分 0 を返す。 呼び出し元は `direction === 'balanced'` で
 * 空メッセージ分岐を判定し、 バーを描かない。
 */
export function computeIntradayGapSegments(
  input: IntradayGapInput,
): IntradayGapSegments {
  const aligned = alignGapInput(input)
  const { needAbsMw } = aligned
  const { matchedMw, submitted, all } = computeAlignedBases(aligned)
  const bidOnlyPlacedMw = submitted.bidOnlyMw
  const bidOnlyDraftMw = Math.max(0, all.bidOnlyMw - submitted.bidOnlyMw)
  const uncoveredMw = Math.max(
    0,
    needAbsMw - matchedMw - bidOnlyPlacedMw - bidOnlyDraftMw,
  )
  return {
    direction: aligned.direction,
    needAbsMw,
    matchedMw,
    bidOnlyPlacedMw,
    bidOnlyDraftMw,
    uncoveredMw,
  }
}

/**
 * 凡例の添え書きと読み上げに出す、 基準 (「入札済み」「全札 (下書き含む)」) ごとの入札のみ・未カバー。
 * 4 区分の足し引きでは出さない。 そのため横棒の区分と一致しないコマがある (同上 Discussion)。
 */
export function computeIntradayGapBases(input: IntradayGapInput): {
  submitted: IntradayGapBasisValues
  all: IntradayGapBasisValues
} {
  const { submitted, all } = computeAlignedBases(alignGapInput(input))
  return { submitted, all }
}

/**
 * 凡例の添え書きと読み上げを「入札済み」基準の文に切り替えるコマか (#11771 Q233)。
 * 横棒の未カバーが「全札 (下書き含む)」基準の未カバーと食い違うコマだけ true で、
 * 下書きの逆向きの札で全札の cover が入札済みを下回るコマがこれに当たる。
 * 値は表示と同じ 0.1 MW 単位で比べ、 浮動小数の誤差では切り替えない。
 */
export function isUncoveredBasisNoteSwitched(input: IntradayGapInput): boolean {
  const { uncoveredMw } = computeIntradayGapSegments(input)
  const { all } = computeIntradayGapBases(input)
  return roundToTenthMw(uncoveredMw) !== roundToTenthMw(all.uncoveredMw)
}

export type IntradayGapSegmentPercents = {
  matchedPct: number
  bidOnlyPlacedPct: number
  bidOnlyDraftPct: number
  uncoveredPct: number
}

/**
 * 4 区分の MW を、 横棒の幅比率 (%、 need に対する割合) に変換する。
 * `needAbsMw === 0` は呼び出し元がバーを描かない分岐だが、 0 除算を避けて全て 0 を返す。
 */
export function computeIntradayGapPercents(
  segments: IntradayGapSegments,
): IntradayGapSegmentPercents {
  if (segments.needAbsMw === 0) {
    return {
      matchedPct: 0,
      bidOnlyPlacedPct: 0,
      bidOnlyDraftPct: 0,
      uncoveredPct: 0,
    }
  }
  const pct = (mw: number): number => (mw / segments.needAbsMw) * 100
  return {
    matchedPct: pct(segments.matchedMw),
    bidOnlyPlacedPct: pct(segments.bidOnlyPlacedMw),
    bidOnlyDraftPct: pct(segments.bidOnlyDraftMw),
    uncoveredPct: pct(segments.uncoveredMw),
  }
}

/**
 * 部分約定のあと取り下げた ([[isWithdrawnBidRow]]) 札を、 約定済み量だけの札に置き換える。
 *
 * 取下は未約定の残量だけを除く。 約定済み量は取引として確定しているので、 約定と
 * 入札時カバー量の両方に数える (domain-knowledge.md「部分約定 + 取消の併存ケース」)。
 * 集計本体の `buildIntradayPositionSlots` が呼び出しの先頭でこの読み替えを通す。
 * 約定の無い取下はそのまま返し、 集計側で除かれる。
 */
export function countCancelledFillsAsPartial(
  bids: readonly IntradayBidRow[],
): IntradayBidRow[] {
  return bids.map((b) => {
    if (!isWithdrawnBidRow(b)) return b
    if (b.execVolumeMw === undefined || Number(b.execVolumeMw) === 0) return b
    return {
      ...b,
      volumeMw: b.execVolumeMw,
      bidStatus: BiddingIntradayOrderStatus.PARTIAL_MATCHED,
      withdrawn: false,
    }
  })
}
